import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { flushSync } from "react-dom";
import {
  AlertTriangle,
  ChevronRight,
  Dumbbell,
  FileText,
  Plus,
  Search,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc, type RouterOutputs } from "@/lib/trpc/client";
import { useWorkoutSession } from "@/lib/states/workout/hooks";
import type { ActiveWorkoutData } from "@/lib/states/workout/store";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { buildWorkoutSessionExercises } from "@/lib/utils/workoutSession";
import { sectionsForSession, toSessionSections } from "@/lib/utils/workoutSectionClock";
import { cn } from "@/lib/utils/ui";

type ClientPlan = RouterOutputs["training"]["listForClient"][number];
type PlanWorkout = ClientPlan["workouts"][number];
type StartableWorkout = Pick<
  PlanWorkout,
  "id" | "name" | "description" | "exercises" | "sections"
>;
type PlanGroup = {
  id: string;
  name: string;
  is_primary: boolean;
  workouts: PlanWorkout[];
};

type TabId = "plan" | "library";

const SOURCE_TABS = [
  { id: "plan", label: "Plan" },
  { id: "library", label: "Library" },
] as const;

type LibraryTemplate = RouterOutputs["workouts"]["list"][number];

interface LibraryWorkoutPicker {
  title: string;
  description?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  onEmpty: () => void;
  onSelectTemplate: (workout: LibraryTemplate) => void;
  analyticsName?: string;
}

interface StartWorkoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: string;
  clientName?: string;
  libraryPicker?: LibraryWorkoutPicker;
}

function exerciseCount(exercises: StartableWorkout["exercises"]) {
  return exercises.length;
}

function formatExerciseCount(count: number) {
  return `${count} ${count === 1 ? "exercise" : "exercises"}`;
}

function matchesQuery(
  query: string,
  ...values: Array<string | null | undefined>
) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return values.some((value) => value?.toLowerCase().includes(normalized));
}

function WorkoutRow({
  name,
  exerciseCount: count,
  disabled,
  onSelect,
}: {
  name: string;
  exerciseCount: number;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 text-left transition-colors",
        "hover:border-primary hover:bg-accent/40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:pointer-events-none disabled:opacity-50",
      )}
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <FileText className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{name}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Dumbbell className="size-3.5 shrink-0" />
          <span>{formatExerciseCount(count)}</span>
        </div>
      </div>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

function EmptyWorkoutRow({
  disabled,
  onSelect,
  title = "Start an empty workout",
  description = "Build a workout from scratch for this session",
}: {
  disabled?: boolean;
  onSelect: () => void;
  title?: string;
  description?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-1 py-2 text-left transition-colors",
        "hover:bg-accent/40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:pointer-events-none disabled:opacity-50",
      )}
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-primary/50 text-primary">
        <Plus className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{title}</div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}

function PlanSectionHeader({
  name,
  isPrimary,
}: {
  name: string;
  isPrimary?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <h3 className="font-semibold">{name}</h3>
      {isPrimary && (
        <Badge
          variant="outline"
          className="rounded-full px-2 py-0 text-[11px] font-medium text-muted-foreground"
        >
          Primary
        </Badge>
      )}
    </div>
  );
}

function PlanGroups({
  plans,
  emptyMessage,
  emptyPlanMessage,
  disabled,
  onSelect,
}: {
  plans: PlanGroup[];
  emptyMessage?: string;
  emptyPlanMessage?: string;
  disabled?: boolean;
  onSelect: (workout: StartableWorkout, planName: string) => void;
}) {
  if (plans.length === 0) {
    return emptyMessage ? (
      <p className="text-sm text-muted-foreground">{emptyMessage}</p>
    ) : null;
  }

  return (
    <>
      {plans.map((plan) => (
        <div key={plan.id} className="space-y-2">
          <PlanSectionHeader name={plan.name} isPrimary={plan.is_primary} />
          {plan.workouts.length === 0 ? (
            emptyPlanMessage ? (
              <p className="text-sm text-muted-foreground">{emptyPlanMessage}</p>
            ) : null
          ) : (
            plan.workouts.map((workout) => (
              <WorkoutRow
                key={workout.id}
                name={workout.name}
                exerciseCount={exerciseCount(workout.exercises)}
                disabled={disabled}
                onSelect={() => onSelect(workout, plan.name)}
              />
            ))
          )}
        </div>
      ))}
    </>
  );
}

function TemplateList({
  workouts,
  emptyMessage,
  disabled,
  onSelect,
}: {
  workouts: StartableWorkout[];
  emptyMessage?: string;
  disabled?: boolean;
  onSelect: (workout: StartableWorkout) => void;
}) {
  if (workouts.length === 0) {
    return emptyMessage ? (
      <p className="text-sm text-muted-foreground">{emptyMessage}</p>
    ) : null;
  }

  return (
    <>
      {workouts.map((workout) => (
        <WorkoutRow
          key={workout.id}
          name={workout.name}
          exerciseCount={exerciseCount(workout.exercises)}
          disabled={disabled}
          onSelect={() => onSelect(workout)}
        />
      ))}
    </>
  );
}

export default function StartWorkoutDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  libraryPicker,
}: StartWorkoutDialogProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const org = useOrg();
  const { startWorkoutSession, activeWorkoutData } = useWorkoutSession();
  const [isStarting, setIsStarting] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabId>("plan");
  const isLibraryPicker = Boolean(libraryPicker);

  const query = search.trim();
  const isSearching = query.length > 0;

  const { data: client } = trpc.clients.get.useQuery(
    { clientId: clientId ?? "" },
    { enabled: Boolean(open && clientId && !clientName?.trim() && !isLibraryPicker) },
  );
  const { data: planRows, isLoading: isPlansLoading } =
    trpc.training.listForClient.useQuery(
      { clientId: clientId ?? "" },
      { enabled: Boolean(open && clientId && !isLibraryPicker) },
    );
  const { data: templates, isLoading: isTemplatesLoading } =
    trpc.workouts.list.useQuery(undefined, {
      enabled: Boolean(
        open && org?.id && (isLibraryPicker || tab === "library" || isSearching),
      ),
    });

  const displayName =
    clientName?.trim() ||
    [client?.first_name, client?.last_name].filter(Boolean).join(" ").trim();

  const plans = useMemo<PlanGroup[]>(() => {
    return (planRows ?? []).map((plan) => ({
      id: plan.id,
      name: plan.name,
      is_primary: plan.is_primary,
      workouts: [...plan.workouts].sort(
        (a, b) => (a.day ?? 0) - (b.day ?? 0),
      ),
    }));
  }, [planRows]);

  const filteredPlans = useMemo(() => {
    if (!isSearching) return plans;
    return plans
      .map((plan) => ({
        ...plan,
        workouts: plan.workouts.filter(
          (workout) =>
            matchesQuery(query, plan.name) ||
            matchesQuery(query, workout.name, workout.description),
        ),
      }))
      .filter((plan) => plan.workouts.length > 0);
  }, [isSearching, plans, query]);

  const libraryWorkouts = templates ?? [];
  const filteredTemplates = useMemo(() => {
    const items = templates ?? [];
    if (!isSearching) return items;
    return items.filter((workout) =>
      matchesQuery(query, workout.name, workout.description),
    );
  }, [isSearching, query, templates]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setTab(isLibraryPicker ? "library" : "plan");
    setIsStarting(false);
  }, [open, isLibraryPicker]);

  const beginSession = async (data: ActiveWorkoutData) => {
    if (!clientId) return;
    setIsStarting(true);
    startWorkoutSession(data);
    flushSync(() => {
      onOpenChange(false);
    });
    try {
      await navigate({ to: "/workout-session" });
    } catch {
      setIsStarting(false);
    }
  };

  const handleStartWorkout = (
    workout: StartableWorkout,
    planName?: string,
  ) => {
    if (!clientId) return;
    const exercises = buildWorkoutSessionExercises(workout.exercises);
    void beginSession({
      clientId,
      clientName: displayName || clientName,
      workoutId: workout.id,
      name: workout.name,
      planName,
      description: workout.description,
      logNotes: "",
      exercises,
      sections: sectionsForSession(
        toSessionSections(workout.sections),
        exercises,
      ),
      startTime: new Date(),
      returnTo: location.pathname,
    });
  };

  const handleStartEmpty = () => {
    if (!clientId) return;
    void beginSession({
      clientId,
      clientName: displayName || clientName,
      name: "New Workout",
      logNotes: "",
      exercises: [],
      startTime: new Date(),
      returnTo: location.pathname,
    });
  };

  const isContentLoading = isLibraryPicker
    ? isTemplatesLoading
    : isSearching
      ? isPlansLoading || isTemplatesLoading
      : tab === "plan"
        ? isPlansLoading
        : isTemplatesLoading;

  if (!open && isStarting) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      analyticsName={libraryPicker?.analyticsName ?? "start_workout_dialog"}
    >
      <DialogContent
        showCloseButton={false}
        className="flex h-[min(40rem,85vh)] max-h-[85vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader
          className={cn(
            "shrink-0 space-y-4 px-6 pt-6 text-left",
            isSearching || isLibraryPicker ? "border-b pb-4" : "pb-0",
          )}
        >
          <div className="pr-10">
            <DialogTitle>
              {libraryPicker?.title ?? "Start workout"}
            </DialogTitle>
            <DialogDescription className="mt-1.5">
              {libraryPicker
                ? libraryPicker.description
                : displayName
                  ? `Choose a workout to log with ${displayName}`
                  : "Choose a workout to log"}
            </DialogDescription>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search workouts"
              aria-label="Search workouts"
              type="search"
              autoFocus
              className="h-10 rounded-lg border-transparent bg-muted/60 pl-9 shadow-none focus-visible:border-input focus-visible:bg-background"
            />
          </div>
          {!isSearching && !isLibraryPicker && (
            <div
              role="tablist"
              aria-label="Workout sources"
              className="-mx-6 flex gap-6 border-b px-6"
            >
              {SOURCE_TABS.map((item) => {
                const isActive = tab === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setTab(item.id)}
                    className={cn(
                      "relative pb-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {item.label}
                    {isActive && (
                      <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </DialogHeader>

        <DialogClose className="absolute right-4 top-4 z-10 rounded-full border border-border p-1.5 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isContentLoading || isStarting ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner className="size-6" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {!isLibraryPicker && activeWorkoutData && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Active workout in progress</AlertTitle>
                  <AlertDescription>
                    Starting a new workout will override the current session.
                  </AlertDescription>
                </Alert>
              )}

              {isLibraryPicker ? (
                <>
                  {!isSearching && (
                    <EmptyWorkoutRow
                      disabled={isStarting}
                      title={libraryPicker?.emptyTitle}
                      description={libraryPicker?.emptyDescription}
                      onSelect={() => libraryPicker?.onEmpty()}
                    />
                  )}
                  <TemplateList
                    workouts={isSearching ? filteredTemplates : libraryWorkouts}
                    emptyMessage={
                      isSearching
                        ? "No workouts match your search."
                        : "No workout templates found in your library."
                    }
                    disabled={isStarting}
                    onSelect={(workout) => {
                      const template = (templates ?? []).find(
                        (item) => item.id === workout.id,
                      );
                      if (template) libraryPicker?.onSelectTemplate(template);
                    }}
                  />
                </>
              ) : isSearching ? (
                <>
                  <PlanGroups
                    plans={filteredPlans}
                    disabled={isStarting}
                    onSelect={handleStartWorkout}
                  />
                  {filteredTemplates.length > 0 && (
                    <div className="space-y-2">
                      <PlanSectionHeader name="Library" />
                      <TemplateList
                        workouts={filteredTemplates}
                        disabled={isStarting}
                        onSelect={handleStartWorkout}
                      />
                    </div>
                  )}
                  {filteredPlans.length === 0 &&
                    filteredTemplates.length === 0 && (
                      <p className="py-8 text-center text-sm text-muted-foreground">
                        No workouts match your search.
                      </p>
                    )}
                </>
              ) : tab === "plan" ? (
                <>
                  <EmptyWorkoutRow
                    disabled={isStarting}
                    onSelect={handleStartEmpty}
                  />
                  <PlanGroups
                    plans={plans}
                    emptyMessage="No training plan workouts found for this client."
                    emptyPlanMessage="No workouts in this plan."
                    disabled={isStarting}
                    onSelect={handleStartWorkout}
                  />
                </>
              ) : (
                <TemplateList
                  workouts={libraryWorkouts}
                  emptyMessage="No workout templates found in your library."
                  disabled={isStarting}
                  onSelect={handleStartWorkout}
                />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
